/**
 * The palette's mode prefixes (`070-search-and-links.md` §4).
 *
 * The FIRST CHARACTER of the input selects the mode — the VS Code / Linear / Raycast
 * convention. It costs no chrome, and it is client-side, so switching mode is instant and
 * is deliberately NOT debounced (only the search legs are).
 *
 * The prefix is never sent to the server: `q` is the text AFTER it.
 *
 * ── A spec discrepancy, recorded rather than silently resolved ──────────────────────
 *
 * `070` §4 assigns `@` to **events** ("Jump to event"). `140-roadmap.md` §M4 acceptance
 * criterion 6 requires "`>` shows commands, `#` shows tags, `@` shows pages".
 *
 * They cannot both be satisfied, and events do not exist until M5 — an `@` that opened an
 * empty event list would be a dead key for a whole milestone. So `@` is **pages** here,
 * per the roadmap, which is what M4 is judged against. When M5 lands the calendar, this is
 * the decision to revisit: either `@` becomes events (and page-jump keeps `Mod+P`, which
 * `110-design-system.md` §7 already binds to "the palette in page-jump mode"), or events
 * take a new prefix. Do not "fix" one document against the other without deciding that.
 */

export type PaletteMode =
  /** Bare input. `GET /api/search` — pages, then events. */
  | 'search'
  /** `>` — a static, client-side, fuzzy-filtered action list. No network. */
  | 'commands'
  /** `#` — filters the cached tag list. Enter navigates to the tag view. */
  | 'tags'
  /** `@` — pages only. See the note above. */
  | 'pages';

export interface PaletteInput {
  mode: PaletteMode;
  /** What to search for: the raw input with its prefix stripped. Never includes the prefix. */
  query: string;
}

const PREFIXES: Record<string, PaletteMode> = {
  '>': 'commands',
  '#': 'tags',
  '@': 'pages',
};

export function parsePaletteInput(raw: string): PaletteInput {
  const mode = PREFIXES[raw.charAt(0)];

  if (mode === undefined) return { mode: 'search', query: raw.trim() };

  // Only the prefix is stripped, not leading spaces the user meant: `# npc` searches for
  // "npc" because the trim runs after the slice, and a tag name cannot start with a space
  // anyway (`070` §6, "Name rules").
  return { mode, query: raw.slice(1).trim() };
}

/** The two modes that hit the network, and therefore the two that get the 150 ms debounce. */
export function isNetworkMode(mode: PaletteMode): boolean {
  return mode === 'search' || mode === 'pages';
}
